import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  ArrowLeft,
  Globe,
  Shield,
  Wrench,
  Database,
  Clock,
  Save,
  RefreshCw,
  Copy,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Upload,
  Check,
  Trash2,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Switch } from '../ui/switch'
import { toast } from 'react-hot-toast'
import { cn } from '../../utils/cn'

const SiteSettings = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)
  const [faviconFile, setFaviconFile] = useState(null)
  const [faviconPreview, setFaviconPreview] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)

  // Fetch site data
  const { data: site, isLoading, error } = useQuery(
    ['site', id],
    async () => {
      console.log('Fetching site data for ID:', id)
      const response = await apiService.get(`/sites/${id}`)
      console.log('API Response:', response)
      return response
    },
    {
      enabled: !!id,
      onSuccess: (data) => {
        console.log('Site data received:', data)
      },
      onError: (error) => {
        console.error('Error fetching site data:', error)
      }
    }
  )

  // Site settings state
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    maintenanceMessage: 'This website is currently under maintenance. Please check back later.',
    passwordProtection: false,
    password: '',
    searchIndexing: true,
    customCode: {
      headerCode: '',
      footerCode: '',
      analyticsCode: ''
    }
  })

  // Initialize settings when site data loads
  useEffect(() => {
    if (site) {
      const meta = site?.data?.meta_data || {}
      setSettings({
        maintenanceMode: meta.maintenanceMode || false,
        maintenanceMessage: meta.maintenanceMessage || 'This website is currently under maintenance. Please check back later.',
        passwordProtection: meta.passwordProtection || false,
        password: meta.accessPassword || '',
        searchIndexing: meta.searchIndexing !== false,
        customCode: {
          headerCode: meta.customCode?.headerCode || '',
          footerCode: meta.customCode?.footerCode || '',
          analyticsCode: meta.customCode?.analyticsCode || ''
        }
      })
    }
  }, [site])

  // Update site settings mutation
  const updateSettingsMutation = useMutation(
    (newSettings) => apiService.put(`/sites/${id}/settings`, newSettings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
        toast.success('Site settings updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings')
        setIsSaving(false)
      }
    }
  )

  // Upload favicon mutation (admin sets on behalf of client)
  const uploadFaviconMutation = useMutation(
    async (formData) => {
      const response = await apiService.post('/clients/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Favicon uploaded successfully!')
        queryClient.invalidateQueries(['site', id])
        setFaviconFile(null)
        setFaviconPreview(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to upload favicon')
      }
    }
  )

  // Upload site logo mutation — uploads file then stores URL in meta_data.logo via PUT /sites/:id
  const uploadLogoMutation = useMutation(
    async (formData) => {
      const uploadRes = await apiService.post('/media/upload', formData)
      const url = uploadRes.data?.files?.[0]?.public_url
      if (!url) throw new Error('No URL returned from upload')
      const rawMeta = site?.data?.meta_data
      const existingMeta = rawMeta
        ? (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta)
        : {}
      await apiService.put(`/sites/${id}`, { meta_data: JSON.stringify({ ...existingMeta, logo: url }) })
      return url
    },
    {
      onSuccess: () => {
        toast.success('Site logo uploaded successfully!')
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
        setLogoFile(null)
        setLogoPreview(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || error.message || 'Failed to upload logo')
      }
    }
  )

  const removeLogoMutation = useMutation(
    async () => {
      const rawMeta = site?.data?.meta_data
      const existingMeta = rawMeta
        ? (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta)
        : {}
      const { logo: _removed, ...metaWithoutLogo } = existingMeta
      await apiService.put(`/sites/${id}`, { meta_data: JSON.stringify(metaWithoutLogo) })
    },
    {
      onSuccess: () => {
        toast.success('Site logo removed')
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to remove logo')
      }
    }
  )

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleLogoUpload = () => {
    if (!logoFile) return
    const formData = new FormData()
    formData.append('files', logoFile)
    const clientId = site?.data?.client_id
    if (clientId) formData.append('client_id', clientId)
    uploadLogoMutation.mutate(formData)
  }

  const handleFaviconChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFaviconFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setFaviconPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleFaviconUpload = () => {
    if (!faviconFile) return
    const clientId = site?.data?.client_id
    const formData = new FormData()
    formData.append('favicon', faviconFile)
    if (clientId) formData.append('clientId', clientId)
    uploadFaviconMutation.mutate(formData)
  }

  const handleSave = () => {
    setIsSaving(true)
    updateSettingsMutation.mutate(settings)
  }

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all settings to default values?')) {
      setSettings({
        maintenanceMode: false,
        maintenanceMessage: 'This website is currently under maintenance. Please check back later.',
        passwordProtection: false,
        password: '',
        searchIndexing: true,
        customCode: { headerCode: '', footerCode: '', analyticsCode: '' }
      })
      toast.success('Settings reset to defaults')
    }
  }

  const siteUrl = site?.data?.assigned_domain
    ? `https://${site.data.assigned_domain}`
    : null

  const copyUrlToClipboard = () => {
    if (siteUrl) {
      navigator.clipboard.writeText(siteUrl)
      toast.success('Site URL copied to clipboard')
    }
  }

  const getStatusText = (status) => {
    if (!status) return 'UNKNOWN'
    try {
      return String(status).toUpperCase()
    } catch (error) {
      console.error('Error converting status to string:', error)
      return 'UNKNOWN'
    }
  }

  if (isLoading) {
    return (
      <div className="pt-4">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading site settings..." />
        </div>
      </div>
    )
  }

  if (error || (!isLoading && !site)) {
    return (
      <div className="pt-4">
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Site Not Found
            </h3>
            <p className="text-red-600 dark:text-red-300 mb-4">
              The requested site could not be found or you don't have permission to access it.
            </p>
            <Button onClick={() => navigate('/admin/sites')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Sites
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Don't render anything if we're still loading or site is null
  if (isLoading || !site) {
    return (
      <div className="pt-4">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading site settings..." />
        </div>
      </div>
    )
  }

  return (
    <div className="pt-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/sites')}
              className="text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                {site?.data?.name || 'Site'} Settings
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400">
                Configure website settings and management options
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline" 
              onClick={handleReset}
              className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41] hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>

        {/* Site Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-accent-600" />
              Site Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Site Name
                </label>
                <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {site?.data?.name || 'Unnamed Site'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Company
                </label>
                <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {site?.data?.company_name || 'Unknown Company'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Status
                </label>
                <span className={cn(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  site?.data?.status === 'published' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : site?.data?.status === 'draft'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200'
                )}>
                  {site?.data?.status === 'published' && <CheckCircle className="h-3 w-3 mr-1" />}
                  {site?.data?.status === 'draft' && <Clock className="h-3 w-3 mr-1" />}
                  {site?.data?.status === 'archived' && <XCircle className="h-3 w-3 mr-1" />}
                  {getStatusText(site?.data?.status)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Site URL
                </label>
                <div className="flex items-center space-x-2">
                  {siteUrl ? (
                    <>
                      <code className="px-2 py-1 bg-neutral-100 dark:bg-neutral-800 rounded text-sm">
                        {siteUrl}
                      </code>
                      <Button size="sm" variant="ghost" onClick={copyUrlToClipboard}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      {site?.data?.status === 'published' && (
                        <Button size="sm" variant="ghost" asChild>
                          <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-neutral-500 dark:text-neutral-400 italic">
                      No domain assigned yet
                    </span>
                  )}
                </div>
                {siteUrl && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    To change the primary domain, go to the client's Domain Management page.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Favicon */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-accent-600" />
              Favicon
              {site?.data?.favicon && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200 rounded-full text-xs font-medium">
                  <Check className="w-3 h-3" />
                  Uploaded
                </span>
              )}
            </CardTitle>
            <CardDescription>
              The icon shown in browser tabs and bookmarks for this client's websites
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-6">
              {/* Current favicon */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Current Favicon
                </label>
                <div className="w-20 h-20 border-2 border-dashed border-neutral-200 dark:border-[#2f2b41] rounded-lg flex items-center justify-center bg-neutral-50 dark:bg-dark-300">
                  {site?.data?.favicon ? (
                    <img src={site.data.favicon} alt="Favicon" className="w-10 h-10 object-contain" />
                  ) : (
                    <div className="text-center">
                      <Globe className="w-6 h-6 text-neutral-400 mx-auto mb-1" />
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">None</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Upload New Favicon
                </label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                  Accepts .ico, .png, or .svg. Square image recommended (32×32px). Applied to all sites for this client.
                </p>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
                    onChange={handleFaviconChange}
                    className="block w-full text-sm text-neutral-500 dark:text-neutral-400
                      file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                      file:text-sm file:font-medium
                      file:bg-accent-50 file:text-accent-700 dark:file:bg-accent-900/20 dark:file:text-accent-300
                      hover:file:bg-accent-100 dark:hover:file:bg-accent-900/30"
                  />
                  {faviconPreview && (
                    <div className="w-20 h-20 border-2 border-neutral-200 dark:border-[#2f2b41] rounded-lg overflow-hidden flex items-center justify-center bg-neutral-50 dark:bg-dark-300">
                      <img src={faviconPreview} alt="Preview" className="w-10 h-10 object-contain" />
                    </div>
                  )}
                  <Button
                    onClick={handleFaviconUpload}
                    disabled={!faviconFile || uploadFaviconMutation.isLoading}
                    size="sm"
                  >
                    {uploadFaviconMutation.isLoading ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-3.5 w-3.5 mr-2" />
                        Upload Favicon
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Site Logo */}
        {(() => {
          const rawMeta = site?.data?.meta_data
          const parsedMeta = rawMeta
            ? (typeof rawMeta === 'string' ? (() => { try { return JSON.parse(rawMeta) } catch { return {} } })() : rawMeta)
            : {}
          const currentLogo = parsedMeta?.logo || null
          return (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-accent-600" />
                  Site Logo
                  {currentLogo && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200 rounded-full text-xs font-medium">
                      <Check className="w-3 h-3" />
                      Uploaded
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  The logo displayed on this site's cards and in the content editor. Changing it here will also update the editor.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Current logo */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Current Logo
                    </label>
                    <div className="w-20 h-20 border-2 border-dashed border-neutral-200 dark:border-[#2f2b41] rounded-xl flex items-center justify-center bg-neutral-50 dark:bg-dark-300">
                      {currentLogo ? (
                        <img src={currentLogo} alt="Site Logo" className="w-14 h-14 object-contain rounded-lg" />
                      ) : (
                        <div className="text-center">
                          <Globe className="w-6 h-6 text-neutral-400 mx-auto mb-1" />
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">None</p>
                        </div>
                      )}
                    </div>
                    {currentLogo && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLogoMutation.mutate()}
                        disabled={removeLogoMutation.isLoading}
                        className="mt-2 w-20 text-error-600 hover:text-error-700 hover:bg-error-50 dark:text-error-400 dark:hover:bg-error-900/20"
                      >
                        {removeLogoMutation.isLoading ? (
                          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Remove
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Upload */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Upload New Logo
                    </label>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                      Accepts .png, .jpg, .svg, or .webp. Transparent PNG recommended. Used on site cards and the content editor.
                    </p>
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={handleLogoChange}
                        className="block w-full text-sm text-neutral-500 dark:text-neutral-400
                          file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                          file:text-sm file:font-medium
                          file:bg-accent-50 file:text-accent-700 dark:file:bg-accent-900/20 dark:file:text-accent-300
                          hover:file:bg-accent-100 dark:hover:file:bg-accent-900/30"
                      />
                      {logoPreview && (
                        <div className="w-20 h-20 border-2 border-neutral-200 dark:border-[#2f2b41] rounded-xl overflow-hidden flex items-center justify-center bg-neutral-50 dark:bg-dark-300">
                          <img src={logoPreview} alt="Preview" className="w-14 h-14 object-contain" />
                        </div>
                      )}
                      <Button
                        onClick={handleLogoUpload}
                        disabled={!logoFile || uploadLogoMutation.isLoading}
                        size="sm"
                      >
                        {uploadLogoMutation.isLoading ? (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-3.5 w-3.5 mr-2" />
                            Upload Logo
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })()}

        {/* Maintenance Mode */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-accent-600" />
              Maintenance Mode
            </CardTitle>
            <CardDescription>
              Put your website in maintenance mode to perform updates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">
                  Enable Maintenance Mode
                </h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Show a maintenance page instead of your website
                </p>
              </div>
              <Switch
                checked={settings.maintenanceMode}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, maintenanceMode: checked }))}
              />
            </div>
            {settings.maintenanceMode && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Maintenance Message
                </label>
                <textarea
                  value={settings.maintenanceMessage}
                  onChange={(e) => setSettings(prev => ({ ...prev, maintenanceMessage: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-dark-300 dark:text-neutral-100"
                  placeholder="Enter the message visitors will see during maintenance..."
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Access Control */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-accent-600" />
              Access Control
            </CardTitle>
            <CardDescription>
              Control who can access your website
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">
                  Password Protection
                </h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Require a password to access the website
                </p>
              </div>
              <Switch
                checked={settings.passwordProtection}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, passwordProtection: checked }))}
              />
            </div>
            {settings.passwordProtection && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Access Password
                </label>
                <input
                  type="password"
                  value={settings.password}
                  onChange={(e) => setSettings(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-dark-300 dark:text-neutral-100"
                  placeholder="Enter access password..."
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">
                  Search Engine Indexing
                </h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Allow search engines to index this website
                </p>
              </div>
              <Switch
                checked={settings.searchIndexing}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, searchIndexing: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Custom Code */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-accent-600" />
              Custom Code
            </CardTitle>
            <CardDescription>
              Add custom HTML, CSS, or JavaScript to your website
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Header Code (Before &lt;/head&gt;)
              </label>
              <textarea
                value={settings.customCode.headerCode}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  customCode: { ...prev.customCode, headerCode: e.target.value }
                }))}
                rows={4}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-dark-300 dark:text-neutral-100 font-mono text-sm"
                placeholder="<!-- Custom CSS, meta tags, etc. -->"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Footer Code (Before &lt;/body&gt;)
              </label>
              <textarea
                value={settings.customCode.footerCode}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  customCode: { ...prev.customCode, footerCode: e.target.value }
                }))}
                rows={4}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-dark-300 dark:text-neutral-100 font-mono text-sm"
                placeholder="<!-- Custom JavaScript, tracking codes, etc. -->"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Analytics Code
              </label>
              <textarea
                value={settings.customCode.analyticsCode}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  customCode: { ...prev.customCode, analyticsCode: e.target.value }
                }))}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-dark-300 dark:text-neutral-100 font-mono text-sm"
                placeholder="<!-- Google Analytics, Facebook Pixel, etc. -->"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

export default SiteSettings